import type { IAdminOrganizationRepository } from "@/application/ports/repositories";
import type {
  AdminOrganization,
  AdminOrganizationUpdateRequest,
  OrgDraftCreateRequest,
  AdminOrganizationMember,
  AdminOrganizationInvitation,
} from "@/domain/admin";
import type {
  AdminEntitlement,
  AdminEntitlementCreateRequest,
  AdminEntitlementUpdateRequest,
  DomainUsageReport,
} from "@/domain/adminUsage";
import { apiClient } from "../client";

export class AdminOrganizationRepository implements IAdminOrganizationRepository {
  private basePath = "/admin/organizations";

  async getOrganizations(): Promise<AdminOrganization[]> {
    const response = await apiClient.get<AdminOrganization[]>(this.basePath);
    return response.data;
  }
  async getOrganization(uuid: string): Promise<AdminOrganization> {
    const response = await apiClient.get<AdminOrganization>(`${this.basePath}/${uuid}`);
    return response.data;
  }
  async createOrganization(data: OrgDraftCreateRequest): Promise<AdminOrganization> {
    const response = await apiClient.post<AdminOrganization>(this.basePath, data);
    return response.data;
  }
  async updateOrganization(
    uuid: string,
    data: AdminOrganizationUpdateRequest,
  ): Promise<AdminOrganization> {
    const response = await apiClient.patch<AdminOrganization>(`${this.basePath}/${uuid}`, data);
    return response.data;
  }
  async deleteOrganization(uuid: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${uuid}`);
  }
  async getOrganizationMembers(organizationUuid: string): Promise<AdminOrganizationMember[]> {
    const response = await apiClient.get<AdminOrganizationMember[]>(
      `${this.basePath}/${organizationUuid}/members`,
    );
    return response.data;
  }
  async getOrganizationInvitations(
    organizationUuid: string,
  ): Promise<AdminOrganizationInvitation[]> {
    const response = await apiClient.get<AdminOrganizationInvitation[]>(
      `${this.basePath}/${organizationUuid}/invitations`,
    );
    return response.data;
  }
  async getOrganizationUsage(
    organizationUuid: string,
    from?: string,
    to?: string,
  ): Promise<DomainUsageReport[]> {
    const query = new URLSearchParams();
    if (from) query.set("from", from);
    if (to) query.set("to", to);
    const qs = query.toString();
    const response = await apiClient.get<DomainUsageReport[]>(
      `${this.basePath}/${organizationUuid}/usage${qs ? `?${qs}` : ""}`,
    );
    return response.data;
  }
  async getOrganizationEntitlements(organizationUuid: string): Promise<AdminEntitlement[]> {
    const response = await apiClient.get<AdminEntitlement[]>(
      `${this.basePath}/${organizationUuid}/entitlements`,
    );
    return response.data;
  }
  async grantEntitlement(
    organizationUuid: string,
    data: AdminEntitlementCreateRequest,
  ): Promise<AdminEntitlement> {
    const response = await apiClient.post<AdminEntitlement>(
      `${this.basePath}/${organizationUuid}/entitlements`,
      data,
    );
    return response.data;
  }
  async updateEntitlement(
    organizationUuid: string,
    moduleCode: string,
    data: AdminEntitlementUpdateRequest,
  ): Promise<AdminEntitlement> {
    const response = await apiClient.patch<AdminEntitlement>(
      `${this.basePath}/${organizationUuid}/entitlements/${moduleCode}`,
      data,
    );
    return response.data;
  }
  async resetEntitlementQuota(
    organizationUuid: string,
    moduleCode: string,
  ): Promise<AdminEntitlement> {
    const response = await apiClient.post<AdminEntitlement>(
      `${this.basePath}/${organizationUuid}/entitlements/${moduleCode}/reset`,
    );
    return response.data;
  }
}
